import { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "../../../Components/Common/datatable/DataTable";
import {
  Autocomplete,
  Checkbox,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ApiService from "../../../utils/fetch";
import ButtonComponent from "../../../Components/Common/button/Button";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Tabs, Tab, Box } from "@mui/material";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import { useNavigate } from "react-router-dom";
import PlantSelectionTree from "./PlantSelectionTree";
import ManualUpdate from "./ManualUpdate";
import ExcelUpdate from "./ExcelUpdate";
import { useSelector } from "react-redux";

function MaterialConfig() {
  const navigate = useNavigate();
  // Tab Control
  const [activeTab, setActiveTab] = useState(0);

  //loading States
  const [isUploading, setIsUploading] = useState(false);

  // File Upload States
  const TemplateRef = useRef(null);
  const positionRef = useRef(null);
  const positionTableRef = useRef(null);

  const [partnerPosition, setPartnerPosition] = useState(null);
  const [plantPosition, setPlantPosition] = useState(null);
  const [productPosition, setProductPosition] = useState(null);
  const [productType, setProductType] = useState(null);
  const [quantityPosition, setQuantityPosition] = useState(null);
  const [rowNumber, setRowNumber] = useState(1);
  const [samplePositionData, setSamplePositionData] = useState([]);

  // Grid States
  const [selectedTemplateRows, setSelectedTemplateRows] = useState([]);
  const [templateDatas, setTemplateDatas] = useState([]);

  const [uploadLoading, setUploadLoading] = useState(false);

  //TreeData
  const [appliedTreeData, setAppliedTreeData] = useState([]);

  // common Pagination States
  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const FormatType = ["BOTTLER KIT"];

  const AppliedSelectedData =
    useSelector((state) => state?.templateTree?.templateAppliedData) || [];


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onPositionGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  };

  const CustomNoRowsOverlay = ({ noRowsMessage }) => {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          fontSize: "18px",
          color: "#000",
        }}
      >
        {noRowsMessage}
      </div>
    );
  };

  //template configuration upload API

  const templateConfigUpload = async (event) => {
    if (isUploading) return;

    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select a file!",
      });
      return;
    }
    setUploadLoading(true);

    const uploadRequestDTO = {
      rowNumber: rowNumber,
    };

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("uploadRequestDTOJson", JSON.stringify(uploadRequestDTO));

    setIsUploading(true);
    try {
      const response = await ApiService.upload(
        "/v1/sample-template-upload",
        formData
      );
      setSamplePositionData(response.data);
    } catch (error) {
      console.error("Error uploading template configuration file:", error);
      MessagePopup({
        icon: "error",
        title: "Error!",
        message:
          "Failed to upload template configuration file. Please ensure the file contains valid data.",
      });
    } finally {
      // Reset loading state
      setUploadLoading(false);
      setIsUploading(false);
    }
  };

  // Column definitions based on samplePositionData
  const PositionDefinition = useMemo(() => {
    if (!Array.isArray(samplePositionData) || samplePositionData.length === 0) {
      return [];
    }

    return samplePositionData.map((item, index) => ({
      field: `col${index}`,
      headerName: item.fieldname || `Column ${index + 1}`,
      minWidth: 150,
      resizable: true,
      flex: 1,
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: true,
      cellRenderer: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <div>{params.value || ""}</div>
        </Tooltip>
      ),
    }));
  }, [samplePositionData]);

  // Create table rows with fieldPosition and fieldname
  const tableRows = useMemo(() => {
    if (!Array.isArray(samplePositionData) || samplePositionData.length === 0) {
      return [];
    }

    const row = samplePositionData.reduce(
      (rowObj, item, index) => {
        rowObj[`col${index}`] = `${item.fieldPosition}. ${item.fieldname}`;
        return rowObj;
      },
      { id: "row-0" }
    );

    return [row];
  }, [samplePositionData]);

  //template configuration get API for Table
  const handleApplySelection = (AppliedSelectedData) => {
    templateTableData(AppliedSelectedData);
    setAppliedTreeData(AppliedSelectedData);
  };

  const templateTableData = async (AppliedSelectedData) => {
    if (!AppliedSelectedData || AppliedSelectedData.length === 0) {
      setTemplateDatas([]);
      return;
    }
    try {
      const response = await ApiService.post(
        `v1/template/fetchConfiguration`,
        AppliedSelectedData
      );
      setTemplateDatas(response.data);
    } catch (error) {
      console.error("Error fetching template data:", error);
    }
  };

  const templateConfigTableDelete = async () => {
    if (selectedTemplateRows.length === 0) {
      MessagePopup({
        icon: "error",
        title: "No Selection",
        message: "Please select at least one row to delete.",
      });
      return;
    }
    const result = await MessagePopup({
      title: "Confirm Delete",
      html: `<p>Are you sure want to Delete the Configuration?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });
    if (!result.isConfirmed) return;

    const locationCustomers = selectedTemplateRows.map((row) => ({
      locationId: row.locationId,
      customerId: row.customerId,
    }));

    const requestBody = {
      locationCustomers: locationCustomers,
    };
    try {
      const response = await ApiService.delete(
        `/v1/template/deleteConfiguration`,
        requestBody
      );
      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Template configuration Deleted successfully.",
      });

      if (TemplateRef.current && TemplateRef.current.api) {
        TemplateRef.current.api.refreshCells({ force: true });
      }
      templateTableData(AppliedSelectedData);
      setSelectedTemplateRows([]);
    } catch (error) {
      console.error("Error Deleting template configuration:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to Delete. Please try again.",
      });
    }
  };

  const templateDefinition = [
    {
      headerName: "",
      checkboxSelection: true,
      width: 40,
      minWidth: 40,
      pinned: "left",
      suppressMenu: true,
      suppressMovable: true,
      headerCheckboxSelection: true,
    },
    {
      field: "customerId",
      headerName: "Forecast Partner",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      valueGetter: (params) => {
        const { customerId, customerName } = params.data;
        return `${customerId || ""}-${customerName || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "locationId",
      headerName: "Plant",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      valueGetter: (params) => {
        const { locationId, locationName } = params.data;
        return `${locationId || ""}-${locationName || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "cpsForecastPartnerId",
      headerName: "Forecast Partner Position",
      sortable: true,
      unSortIcon: true,
      width: 260,
      minWidth: 260,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "cpsPlantId",
      headerName: "Plant Position",
      sortable: true,
      unSortIcon: true,
      width: 190,
      minWidth: 190,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "productId",
      headerName: "Product Position",
      sortable: true,
      unSortIcon: true,
      width: 210,
      minWidth: 210,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "conversionType",
      headerName: "Product Type",
      sortable: true,
      unSortIcon: true,
      width: 180,
      minWidth: 180,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "forecastQuantityStart",
      headerName: "Forecast Quantity Position",
      sortable: true,
      unSortIcon: true,
      width: 270,
      minWidth: 270,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
  ];

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  useEffect(() => {
    if (
      TemplateRef.current &&
      TemplateRef.current.api &&
      templateDatas.length > 0
    ) {
      TemplateRef.current.api.sizeColumnsToFit();
    }
  }, [templateDatas]);

  const onTemplateSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    console.log(selected, "selected");
    setSelectedTemplateRows(selected);
  };

  const templateConfigUploadSave = async () => {
    if (
      !partnerPosition ||
      !plantPosition ||
      !productPosition ||
      !quantityPosition ||
      !productType
    ) {
      MessagePopup({
        icon: "error",
        title: "Missing Fields",
        message: "Please select all required fields.",
      });
      return;
    }
    if (appliedTreeData.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Please select at least one plant from Selection.",
      });
      return;
    }

    const positions = [
      partnerPosition.fieldPosition,
      plantPosition.fieldPosition,
      productPosition.fieldPosition,
      quantityPosition.fieldPosition,
    ];

    const uniquePositions = new Set(positions);

    if (uniquePositions.size < positions.length) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message:
          "Duplicate values detected. Please ensure all values are unique.",
      });
      return;
    }

    const result = await MessagePopup({
      title: "Confirm Save",
      html: `<p>Are you sure want to save the Configuration?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });
    if (!result.isConfirmed) return;

    const requestBody = {
      locationCustomers: appliedTreeData,
      conversionType: productType,
      cpsForecastPartnerId: partnerPosition.fieldPosition,
      cpsPlantId: plantPosition.fieldPosition,
      productId: productPosition.fieldPosition,
      forecastQuantityStart: quantityPosition.fieldPosition,
    };

    try {
      const response = await ApiService.post(
        `/v1/template/saveConfiguration`,
        requestBody
      );
      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Template configuration saved successfully.",
      });

      setPartnerPosition(null);
      setPlantPosition(null);
      setProductPosition(null);
      setProductType(null);
      setQuantityPosition(null);

      templateTableData(appliedTreeData);
    } catch (error) {
      console.error("Error saving template configuration:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to Save. Please try again.",
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                fontSize: "14px",
                textTransform: "none",
                minWidth: "120px",
              },
            }}
          >
            <Tab label="Template Configuration" />
            <Tab label="Manual Update" />
            <Tab label="Excel Update" />
          </Tabs>
        </Box>
        <ButtonComponent
          label="Back"
          bgColor="black"
          color="white"
          // In your navigation handler:
          onClick={() =>
            navigate("/bf-manage-forecast", { state: { activeTab: 1 } })
          }
          sx={{ ml: 2 }} // Add margin left for spacing
        />
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 0}>
        <Grid container spacing={1}>
          <Grid item xs={8}>
            {/* First Row */}
            <div className="container">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h10"
                  sx={{
                    fontWeight: 450,
                    //fontStyle: "italic",
                  }}
                >
                  Set Column Positions by Upload
                </Typography>
                <ButtonComponent
                  label={isUploading ? "Uploading..." : "File Upload"}
                  bgColor="black"
                  color="white"
                  onClick={() => {
                    if (positionRef.current) {
                      positionRef.current.value = "";
                      positionRef.current.click();
                    }
                  }}
                  disabled={isUploading}
                  startIcon={<FileUploadIcon />}
                />
                <input
                  type="file"
                  ref={positionRef}
                  style={{ display: "none" }}
                  accept=".xlsx,.xls"
                  onChange={templateConfigUpload}
                />
                <Grid item xs={3} sx={{ mt: 0.25 }}>
                  <TextField
                    id="rowReader"
                    label="Excel Row Reader"
                    type="number"
                    value={rowNumber}
                    onChange={(e) => setRowNumber(e.target.value)}
                    InputProps={{
                      style: { fontSize: "13px" },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        transform: "translate(14px, -14px) scale(0.75)",
                      },
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Box>
              <DataTable
                ref={positionTableRef}
                columns={PositionDefinition}
                rows={tableRows || []}
                tableHeight={"15vh"}
                suppressMovableColumns={true}
                suppressHorizontalScroll={false}
                suppressPaginationPanel={true}
                pagination={pagination}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                onGridReady={onPositionGrid}
                headerHeight={0}
                noRowsOverlayComponent={CustomNoRowsOverlay}
                noRowsOverlayComponentParams={{
                  noRowsMessage: "",
                }}
              />
              {/* First Row */}
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ mb: 2, mt: 1 }}
              >
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={samplePositionData}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              partnerPosition?.fieldPosition &&
                            option.fieldname === partnerPosition?.fieldname
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      onChange={(event, newValue) => {
                        setPartnerPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Forecast Partner ID*"
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={samplePositionData}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              plantPosition?.fieldPosition &&
                            option.fieldname === plantPosition?.fieldname
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      onChange={(event, newValue) => {
                        setPlantPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Plant ID*"
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Second Row */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={samplePositionData}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              productPosition?.fieldPosition &&
                            option.fieldname === productPosition?.fieldname
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      onChange={(event, newValue) => {
                        setProductPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Product ID*"
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={FormatType}
                      size="small"
                      value={productType}
                      onChange={(event, newValue) => {
                        setProductType(newValue);
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Product Type*"
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Third Row */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      options={samplePositionData}
                      size="small"
                      value={
                        samplePositionData.find(
                          (option) =>
                            option.fieldPosition ===
                              quantityPosition?.fieldPosition &&
                            option.fieldname === quantityPosition?.fieldname
                        ) || null
                      }
                      getOptionLabel={(option) =>
                        `${option.fieldPosition}. ${option.fieldname}` || ""
                      }
                      onChange={(event, newValue) => {
                        setQuantityPosition(
                          newValue
                            ? {
                                fieldPosition: newValue.fieldPosition,
                                fieldname: newValue.fieldname,
                              }
                            : null
                        );
                      }}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Forecast Quantity Start*"
                          InputLabelProps={{
                            shrink: true,
                            style: {
                              transform: "translate(14px, -14px) scale(0.75)",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <ButtonComponent
                      bgColor="black"
                      color="white"
                      label="Save"
                      onClick={templateConfigUploadSave}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <div className="container">
              <Grid
                container
                spacing={1}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "16px",
                }}
              >
                <Grid
                  item
                  xs={12}
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <ButtonComponent
                    label="Delete"
                    bgColor="black"
                    color="white"
                    onClick={templateConfigTableDelete}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <DataTable
                    ref={TemplateRef}
                    columns={templateDefinition}
                    rows={templateDatas || []}
                    tableHeight={"60vh"}
                    suppressMovableColumns={true}
                    suppressHorizontalScroll={false}
                    suppressPaginationPanel={false}
                    pagination={pagination}
                    paginationPageSize={paginationPageSize}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    onGridReady={onGrid}
                    rowSelection="multiple"
                    onSelectionChanged={onTemplateSelectionChanged}
                    getRowId={(params) => params.data.id}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
          <Grid item xs={4} spacing={1}>
            <PlantSelectionTree
              onApplySelection={handleApplySelection}
              title="Selection"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tab 1 */}
      <Box role="tabpanel" hidden={activeTab !== 1}>
        <ManualUpdate activeTab={activeTab} />
      </Box>

      {/* Tab 2 */}
      <Box role="tabpanel" hidden={activeTab !== 2}>
        <ExcelUpdate />
      </Box>
    </>
  );
}

export default MaterialConfig;
